import { useCallback, useState } from 'react'
import type { UseFormReturn } from 'react-hook-form'

import type { EditBookingFormValues } from '../utils/editBookingValidation'

import type { FindDriverData } from './useFindDriver'
import { useFindDriver } from './useFindDriver'

type UseFindDriverFlowArgs = {
  form: UseFormReturn<EditBookingFormValues>
}

export type UseFindDriverFlowResult = {
  pendingDriverData: FindDriverData | null
  handleDriverPhoneChange: (phone: string, driverName: string) => void
  onConfirmDriver: () => void
  onCancelDriver: () => void
  isFindDriverPending: boolean
}

export const useFindDriverFlow = ({ form }: UseFindDriverFlowArgs): UseFindDriverFlowResult => {
  const [pendingDriverData, setPendingDriverData] = useState<FindDriverData | null>(null)
  const findDriverMutation = useFindDriver()

  const handleDriverPhoneChange = useCallback(
    (phone: string, driverName: string) => {
      if (form.getValues('driverId') != null) return
      const normalized = phone.replace(/-/g, '')
      const isHalfWidth = /^[0-9]+$/.test(normalized)
      if (normalized.length !== 11 || !isHalfWidth) return
      if (findDriverMutation.isPending) return

      findDriverMutation.mutate(
        { driverPhone: normalized, driverName },
        {
          onSuccess: (data) => {
            if (data) setPendingDriverData(data)
          },
        }
      )
    },
    [findDriverMutation, form]
  )

  const onConfirmDriver = useCallback(() => {
    if (!pendingDriverData) return
    form.setValue('driverId', pendingDriverData.driverId, { shouldDirty: true })
    if (pendingDriverData.driverCompanyId) {
      form.setValue('driverCompanyId', pendingDriverData.driverCompanyId, { shouldDirty: true })
    }
    if (pendingDriverData.driverCompanyName) {
      form.setValue('driverCompanyName', pendingDriverData.driverCompanyName, {
        shouldDirty: true,
      })
    }
    form.setValue('driverName', pendingDriverData.driverName ?? '', { shouldDirty: true })
    form.setValue('driverEmail', pendingDriverData.driverEmail ?? '', { shouldDirty: true })
    setPendingDriverData(null)
  }, [pendingDriverData, form])

  const onCancelDriver = useCallback(() => {
    form.setValue('driverPhone', '', { shouldDirty: true })
    setPendingDriverData(null)
  }, [form])

  return {
    pendingDriverData,
    handleDriverPhoneChange,
    onConfirmDriver,
    onCancelDriver,
    isFindDriverPending: findDriverMutation.isPending,
  }
}
